import { Button, CircularProgress, Fade, Grid, TextField } from "@material-ui/core"
import { Elements, PhoneNumberInput, SplitButton, TextButton } from "@tfo/common"
import { IOrderClient } from "@tfo/common/src/models"
import React, { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import $http from "../../../../../../../utils/$http"
import { regex } from "../../../../../../../utils/regex"
import CustomerPortalDialog from "../../../../../common/CustomerPortalDialog"
import DepersonalizeDialog from "./DepersonalizeDialog"
import personnummer from 'personnummer.js'

import "./ReportsItemIndividualDialog.scss"
const { Label } = Elements

type CommunicationType = 'sms' | 'email'

const ReportsItemIndividualDialog = ({ open, onClose, selectedOrder, updateOrder, removeOrder }: { open: boolean, onClose: () => void, selectedOrder: IOrderClient, updateOrder: (order: IOrderClient) => void, removeOrder: (order: IOrderClient) => void }) => {
    const intl = useIntl()
    const initialEmail = selectedOrder.candidate.email
    const initialPhoneNumber = selectedOrder.candidate.phoneNumber

    const [order, setOrder] = useState<IOrderClient>(selectedOrder)
    const [latestApprovalDate, setLatestApprovalDate] = useState<Date>()
    const [disabled, setDisabled] = useState<boolean>(true)
    const [patchLoading, setPatchLoading] = useState<boolean>(false)
    const [reminderLoading, setReminderLoading] = useState<boolean>(false)
    const [disableButtons, setDisableButtons] = useState<boolean>(false)
    const [responseMessage, setResponseMessage] = useState<string>('')
    const [depersonalizeModal, setDepersonalizeModal] = useState<boolean>(false)
    const [invalidPhoneNumber, setInvalidPhoneNumber] = useState<boolean>(false)
    const [isValidNationalId, setIsValidNationalId] = useState<boolean>(true)

    useEffect(() => {
        if (open && !order.isInternationalOrder) {
            getLatestApprovalEmail()
        }
    }, [open])

    const getLatestApprovalEmail = async () => {
        $http.get<{ created: Date }>(`/notification/history/approval/${order.id}/latest`)
            .then((latestApproval) => setLatestApprovalDate(latestApproval.created))
            .catch(() => {
                setLatestApprovalDate(undefined)
            })
    }

    useEffect(() => {
        setDisabled(getDisabled())
    }, [order.candidate?.firstName, order.candidate?.lastName, order.candidate?.email, order.candidate?.phoneNumber, order.candidate?.nationalId, invalidPhoneNumber, isValidNationalId])

    const getDisabled = (): boolean => {
        return isInvalid(order.candidate?.firstName) || isInvalid(order.candidate?.lastName) || !regex.email.test(order.candidate?.email || '') || invalidPhoneNumber || !isValidNationalId
    }

    const isInvalid = (value: string): boolean => {
        return value === undefined || value === null || value === ''
    }

    const validateNationalId = (event: React.ChangeEvent<HTMLInputElement>): boolean => {
        const value = event.target.value

        const parsedValue = personnummer.parse(value)
        const isValidNationalId = personnummer.validate(parsedValue.normalised)

        const isValidAge = parsedValue.age >= 18 && parsedValue.age < 110
        const isCompletelyValid = isValidNationalId && isValidAge

        setIsValidNationalId(isCompletelyValid)

        if (isCompletelyValid) {
            setOrder({ ...order, candidate: { ...order.candidate, nationalId: { ...order.candidate.nationalId, value: value } } })
        }

        return isCompletelyValid
    }

    const validateBirthDate = (event: React.ChangeEvent<HTMLInputElement>): boolean => {
        const value = event.target.value

        const formatRegex = /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/
        const isValidFormat = formatRegex.test(value)

        if (!isValidFormat) {
            setIsValidNationalId(false)
            return false
        }

        const inputDate = new Date(value)
        const today = new Date()

        const maxAge = new Date(today.getFullYear() - 110, today.getMonth(), today.getDate())
        const minAge = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())

        const isValidAge = inputDate >= maxAge && inputDate <= minAge
        const isValidDate = !isNaN(inputDate.getTime()) && isValidAge

        setIsValidNationalId(isValidDate)

        if (isValidDate) {
            setOrder({ ...order, candidate: { ...order.candidate, nationalId: { ...order.candidate.nationalId, value: value } } })
        }

        return isValidDate
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setOrder({ ...order, candidate: { ...order.candidate, [event.target.name]: event.target.value } })
    }

    const handlePhoneNumberChange = (value: string, invalid: boolean): void => {
        setOrder({ ...order, candidate: { ...order.candidate, phoneNumber: value } })
        setInvalidPhoneNumber(invalid)
    }

    const sendEmailReminder = (): Promise<any> => {
        return $http.post<any>('/notification/send/approval-reminder/individual', { orderId: order.id, preferredCommunicationType: 'Email' })
    }

    const sendSmsReminder = (): Promise<any> => {
        return $http.post<any>('/notification/send/approval-reminder/individual', { orderId: order.id, preferredCommunicationType: 'Sms' })
    }

    const patchIndividual = async (): Promise<void> => {
        if (getDisabled()) {
            return
        }

        setPatchLoading(true)
        setDisableButtons(true)
        $http.put(`/user/individual-information/${order.candidate.id}`, getIndividualData())
            .then(() => {
                updateOrder(order)
                setPatchLoading(false)
                setResponseMessage('saved')
                if (!order.isInternationalOrder && order.candidate.email !== initialEmail) {
                    sendEmailReminder()
                }
                if (!order.isInternationalOrder
                    && order.candidate.phoneNumber
                    && order.candidate.phoneNumber !== initialPhoneNumber) {
                    sendSmsReminder()
                }
                setTimeout(() => {
                    onClose()
                    setDisableButtons(false)
                    setResponseMessage('')
                }, 2000)
            })
            .catch(() => {
                setPatchLoading(false)
                setResponseMessage('failed')
                setTimeout(() => {
                    setDisableButtons(false)
                    setResponseMessage('')
                }, 2000)
            })
    }

    const getIndividualData = () => {
        return {
            firstName: order.candidate.firstName,
            lastName: order.candidate.lastName,
            email: order.candidate.email,
            language: order.candidate.language,
            phoneNumber: order.candidate.phoneNumber,
            nationalId: order.candidate.nationalId
        }
    }

    const sendNewApprovalReminder = async (type: CommunicationType): Promise<void> => {
        setReminderLoading(true)
        setDisableButtons(true)

        const sendReminder = type === 'email'
            ? sendEmailReminder
            : sendSmsReminder

        sendReminder()
            .then(() => {
                setResponseMessage('sent')
                setReminderLoading(false)
                setTimeout(() => {
                    setDisableButtons(false)
                    setResponseMessage('')
                    onClose()
                }, 2000)
            })
            .catch(() => {
                setResponseMessage('failed')
                setReminderLoading(false)
                setTimeout(() => {
                    setDisableButtons(false)
                    setResponseMessage('')
                }, 2000)
            })
    }

    const sendApprovalDisabled = (): boolean => {
        return !latestApprovalDate || (Math.abs(new Date(latestApprovalDate).getTime() - new Date().getTime()) / 36e5) < 5
    }

    return (
        <>
            {order?.candidate &&
                <CustomerPortalDialog
                    open={open}
                    onClose={onClose}
                    title={intl.formatMessage({ id: 'reportsitem.individual.dialog.title' })}
                    content={
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {order.candidate.nationalId.value !== '' ? (
                                    order.candidate.nationalId.country === "Other" ? (
                                        <TextField fullWidth label={intl.formatMessage({ id: 'common.birthDate' })} name="birthDate" type="text" variant="outlined" defaultValue={order.candidate.nationalId.value} error={!isValidNationalId} onChange={validateBirthDate}/>
                                    )
                                        : (
                                            <TextField fullWidth label={intl.formatMessage({ id: 'common.nationalid' })} name="nationalId" type="text" variant="outlined" defaultValue={order.candidate.nationalId.value} error={!isValidNationalId} onChange={validateNationalId} />
                                        )
                                ) : null}
                            </Grid>

                            <Grid item xs={6}>
                                <TextField required fullWidth label={intl.formatMessage({ id: 'neworder.firstname.placeholder' })} name="firstName" type="text" variant="outlined" defaultValue={order.candidate.firstName} error={isInvalid(order.candidate.firstName)} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField required fullWidth label={intl.formatMessage({ id: 'neworder.lastname.placeholder' })} name="lastName" type="text" variant="outlined" defaultValue={order.candidate.lastName} error={isInvalid(order.candidate.lastName)} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField required fullWidth label={intl.formatMessage({ id: 'common.email' })} name="email" type="text" variant="outlined" defaultValue={order.candidate.email} error={!regex.email.test(order.candidate.email)} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <PhoneNumberInput
                                    initialCountry='SE'
                                    initialValue={order.candidate.phoneNumber}
                                    onBlur={(value, invalid) => handlePhoneNumberChange(value, invalid)}
                                    placeholder={intl.formatMessage({
                                        id: `common.phonenumber.mobile`,
                                    })}
                                    className='phoneNumber'
                                />
                                {!order.candidate.phoneNumber && <p className={'phonenumber-empty-info'}>
                                    {intl.formatMessage({ id: 'reportsitem.individual.dialog.phonenumber.empty.info', })}
                                </p>}
                            </Grid>
                            <Grid item xs={6}>
                                <TextButton grey onClick={() => setDepersonalizeModal(true)}>{intl.formatMessage({ id: 'reportsitem.individual.dialog.button.depersonalize' })}</TextButton>
                            </Grid>
                            <Grid item xs={6} style={{ display: 'flex', justifyContent: 'end' }}>
                                <TextButton disabled={disabled || disableButtons} onClick={patchIndividual} loading={patchLoading}>{intl.formatMessage({ id: 'reportsitem.individual.dialog.button.save' })}</TextButton>
                            </Grid>
                            <DepersonalizeDialog openModal={depersonalizeModal} dialogTitle={intl.formatMessage({ id: 'reportsitem.individual.depersonalize.dialog.title.preApproved' }, { candidateName: order?.candidate?.firstName + ' ' + order?.candidate?.lastName })} dialogBody="reportsitem.individual.depersonalize.dialog.content" closeModal={() => setDepersonalizeModal(false)} orderId={order.id} individual={order.candidate} depersonalizationCompleted={() => removeOrder(order)} />
                        </Grid>
                    }
                    footer={
                        <Grid container spacing={2} alignItems='center'>
                            {(!order.isInternationalOrder) &&
                                <>
                                    {!reminderLoading ?
                                        <>
                                            {latestApprovalDate ?
                                                <Grid item xs={6}>
                                                    <small>{intl.formatMessage({ id: 'reportsitem.individual.dialog.send.email.content' })} {(intl.formatDate(latestApprovalDate) ?? '') + ' ' + (intl.formatTime(latestApprovalDate) ?? '')}</small>
                                                </Grid> : <></>}
                                            <Grid item xs={6} style={{ display: 'grid' }}>
                                                {order.candidate.phoneNumber
                                                    ? (
                                                        <SplitButton
                                                            style={{ textTransform: 'none' }}
                                                            menuItems={[
                                                                {
                                                                    children: intl.formatMessage({ id: 'reportsitem.individual.dialog.button.send.email' }),
                                                                    props: {
                                                                        onClick: () => sendNewApprovalReminder('email'),
                                                                        dense: true,
                                                                    }
                                                                }]}
                                                            disabled={sendApprovalDisabled() || disableButtons}
                                                            variant={'outlined'}
                                                            color={'default'}

                                                            onClick={() => sendNewApprovalReminder('sms')}>
                                                            {intl.formatMessage({ id: 'reportsitem.individual.dialog.button.send.sms' })}
                                                        </SplitButton>)
                                                    : (
                                                        <Button disabled={sendApprovalDisabled() || disableButtons}
                                                            onClick={() => sendNewApprovalReminder('email')}
                                                            variant={'outlined'}
                                                            size={'small'}

                                                            style={{ textTransform: 'none' }}
                                                            color={'default'}>
                                                            {intl.formatMessage({ id: 'reportsitem.individual.dialog.button.send.email' })}
                                                        </Button>
                                                    )}
                                                {sendApprovalDisabled() && latestApprovalDate &&
                                                    <small style={{ color: '#009B48' }}>{intl.formatMessage({ id: 'reportsitem.individual.dialog.send.email.disabled' })}</small>
                                                }
                                            </Grid>
                                        </>
                                        :
                                        <Grid item xs={12} style={{ justifyContent: 'center', display: 'flex' }}>
                                            <CircularProgress style={{ color: '#009B48' }} size={35} />
                                        </Grid>
                                    }
                                </>
                            }
                            {responseMessage !== '' &&
                                <Fade in={responseMessage !== ''} timeout={{ enter: 300, exit: 1000 }}>
                                    <Grid item xs={12} style={{ textAlign: 'center', paddingTop: '1rem' }}>
                                        <Label style={responseMessage === 'failed' ? { color: 'red' } : {}}>{intl.formatMessage({ id: `response.${responseMessage}` })}</Label>
                                    </Grid>
                                </Fade>
                            }
                        </ Grid>
                    } />
            }
        </>
    )
}

export default ReportsItemIndividualDialog
